import React, { useEffect, useRef, useState } from "react";
import { Select, MenuItem, FormControl } from "@mui/material";
import { CohortRoles, UserRoles } from "../../../constants";
import { useKeycloak } from "@react-keycloak/web";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateRole } from "../../../tanstackQuery/queries/superAdmin/roles";
import { toast } from "sonner";
import { debounce } from "lodash";

interface RoleDropdownProps {
  user?: { roleName: string };
  roleInfo: {
    roleName:string,
    groupType:string,
    userID:string,
    roleID:string,
    groupID:string
  };
  onChange?: (role: string) => void; 
}

const RoleDropdown: React.FC<RoleDropdownProps> = ({ roleInfo }) => {
  const {roleName, groupType, userID, roleID, groupID } = roleInfo;
  const [role, setRole] = useState<string>(roleName || "");
  const { keycloak } = useKeycloak();
  const client = useQueryClient();
  const roles = groupType === "Cohort" ? CohortRoles : UserRoles;
  const isRoleChanged = useRef(false);
  
  const {
    mutateAsync: updateCohort,
    isPending,
    isSuccess,
  } = useUpdateRole(
    { accessToken: keycloak?.token },
    {
      onSuccess: () => {
        client.invalidateQueries({ queryKey: ["orgCohortUsertDetails"] });
        toast.success("Role successfully updated!");
      },
      onError: () => {
        toast.error("Failed to update Role.");
      },
    }
  );

  const updateUsersRole = useRef(
    debounce(async (updatedRole) => {
      if (isRoleChanged.current) {
        try {
          await updateCohort({
            userID,
            roleID,
            groupID,
            roleName: updatedRole,
          });
        } catch (error) {
          toast.error(error?.response?.data?.message || "Failed to update Role.");
        }
      }
    }, 700) 
  ).current;


  useEffect(() => {
    if (roleName) {
      setRole(roleName);
      isRoleChanged.current = false;
    }
  }, [roleName]);

  return (
    <FormControl fullWidth variant="standard">
      <Select
        value={role}
        onChange={(event) => {
          setRole(event.target.value);
          updateUsersRole(event.target.value)
          isRoleChanged.current = true;
        }}
        displayEmpty
        sx={{ border: 0 }}
      >
        <MenuItem value="" disabled>
          <em>Select Role</em>
        </MenuItem>
        {Object.entries(roles).map(([roleName, roleValue]) => (
          <MenuItem key={roleValue} value={roleValue} disabled={["Administrator", "SystemAdministrator"].includes(roleValue)}>
            {roleName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default RoleDropdown;
